import React from 'react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      title: 'Business & Operational Consulting',
      description: 'Professional consulting services focused on improving business operations, organizational efficiency, and process optimization. We help businesses develop sustainable operational frameworks and implement best practices.',
      features: [
        'Operational process analysis and improvement',
        'Organizational structure consulting',
        'Business efficiency assessments',
        'Workflow optimization strategies'
      ]
    },
    {
      title: 'Financial Research & Analysis',
      description: 'Comprehensive research and analytical services designed to support business planning and strategic decision-making. Our research provides data-driven insights and professional documentation.',
      features: [
        'Business and industry research',
        'Financial data analysis and reporting',
        'Market and competitive analysis',
        'Custom research projects'
      ]
    },
    {
      title: 'Strategic Planning & Documentation Support',
      description: 'Expert support for strategic planning initiatives, business documentation, and organizational development. We assist in creating professional frameworks and comprehensive planning materials.',
      features: [
        'Strategic planning development',
        'Business documentation preparation',
        'Policy and procedure development',
        'Professional report writing'
      ]
    }
  ];

  return (
    <div className="page-wrapper">
      <section className="page-header">
        <div className="header-container">
          <h1 className="page-title">Services</h1>
          <p className="page-subtitle">
            Professional consulting and research services for your business
          </p>
        </div>
      </section>

      <section className="services-section">
        <div className="services-container">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <h2 className="service-title">{service.title}</h2>
              <p className="service-description">{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="service-feature">{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing-section">
        <div className="pricing-container">
          <h2 className="pricing-section-title">Engagement & Pricing</h2>
          
          <div className="pricing-intro-box">
            <h3 className="pricing-intro-heading">Engagement Options</h3>
            <p className="pricing-intro">
              ArcaCapitalis operates on a professional consulting engagement basis. All services 
              begin with a paid consultation to assess scope, objectives, and fit.
            </p>
          </div>

          {/* Consultation Requirement */}
          <div className="consultation-box">
            <h3 className="consultation-title">🔹 Consultation (Required)</h3>
            <div className="consultation-card">
              <h4 className="consultation-heading">Initial Strategy Consultation — $150 (60 minutes)</h4>
              <ul className="consultation-features">
                <li>Business assessment</li>
                <li>Problem identification</li>
                <li>Strategic direction</li>
                <li>Service recommendations</li>
                <li>Applied toward first engagement if retained</li>
              </ul>
              <p className="consultation-note">
                Consultation required prior to all engagements.
              </p>
            </div>
          </div>

          {/* Tiered Service Pricing */}
          <div className="tiered-services-section">
            <h3 className="section-heading">🔹 Tiered Service Pricing</h3>
            <div className="pricing-grid">
              <div className="pricing-card">
                <h4 className="pricing-card-title">🔹 Strategic Research</h4>
                <p className="pricing-card-price">Starting at $750 per engagement</p>
                <div className="pricing-card-content">
                  <ul className="pricing-features">
                    <li>Market & competitive research</li>
                    <li>Business intelligence analysis</li>
                    <li>Strategic planning documentation</li>
                    <li>Decision-support reporting</li>
                  </ul>
                  <p className="pricing-card-note">
                    Final pricing based on scope and depth.
                  </p>
                </div>
              </div>

              <div className="pricing-card">
                <h4 className="pricing-card-title">🔹 Operational Structuring</h4>
                <p className="pricing-card-price">Starting at $1,250 per engagement</p>
                <div className="pricing-card-content">
                  <ul className="pricing-features">
                    <li>Operational framework design</li>
                    <li>Process optimization</li>
                    <li>Business structure documentation</li>
                    <li>Efficiency and scalability planning</li>
                  </ul>
                  <p className="pricing-card-note">
                    Custom pricing based on complexity.
                  </p>
                </div>
              </div>

              <div className="pricing-card">
                <h4 className="pricing-card-title">🔹 Business Consulting</h4>
                <p className="pricing-card-price">Starting at $1,500 per engagement</p>
                <div className="pricing-card-content">
                  <ul className="pricing-features">
                    <li>Operational improvement</li>
                    <li>Strategic execution support</li>
                    <li>Process alignment</li>
                    <li>Business development advisory</li>
                  </ul>
                  <p className="pricing-card-note">
                    Engagement-based pricing.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Retainer Packages */}
          <div className="retainer-section">
            <h3 className="section-heading">🔹 Retainer Packages</h3>
            <div className="pricing-grid">
              <div className="pricing-card retainer-card">
                <h4 className="pricing-card-title">🔹 Monthly Advisory Retainer</h4>
                <p className="pricing-card-price">$1,000 / month</p>
                <div className="pricing-card-content">
                  <ul className="pricing-features">
                    <li>Ongoing strategic advisory</li>
                    <li>Email + scheduled check-ins</li>
                    <li>Light research & planning support</li>
                    <li>Priority response</li>
                  </ul>
                </div>
              </div>

              <div className="pricing-card retainer-card">
                <h4 className="pricing-card-title">🔹 Growth Retainer</h4>
                <p className="pricing-card-price">$2,500 / month</p>
                <div className="pricing-card-content">
                  <ul className="pricing-features">
                    <li>Strategic & operational advisory</li>
                    <li>Research support</li>
                    <li>Documentation & planning</li>
                    <li>Bi-weekly strategy sessions</li>
                    <li>Priority client status</li>
                  </ul>
                </div>
              </div>

              <div className="pricing-card retainer-card">
                <h4 className="pricing-card-title">🔹 Enterprise Retainer</h4>
                <p className="pricing-card-price">Custom Pricing</p>
                <div className="pricing-card-content">
                  <ul className="pricing-features">
                    <li>Full advisory support</li>
                    <li>Research, planning & execution</li>
                    <li>Dedicated consulting access</li>
                    <li>Custom deliverables</li>
                  </ul>
                  <p className="pricing-card-note">
                    Pricing determined after consultation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment & Engagement Terms */}
          <div className="payment-terms-section">
            <h3 className="section-heading">🔹 Payment & Engagement Terms</h3>
            <ul className="payment-terms-list">
              <li>Invoicing issued prior to engagement</li>
              <li>Payment due before work begins</li>
              <li>Retainers billed monthly</li>
              <li>No refunds once services commence</li>
            </ul>
          </div>

          {/* Call to Action */}
          <div className="pricing-cta-section">
            <Link to="/contact" className="btn-primary btn-large">
              👉 Book a Consultation
            </Link>
            <Link to="/contact" className="btn-secondary btn-large">
              👉 Contact Us
            </Link>
          </div>
        </div>
      </section>

      <section className="services-note">
        <div className="note-container">
          <p className="note-text">
            ArcaCapitalis provides strategic research, operational structuring, and business 
            consulting services. We do not provide legal, tax, or investment advice.
          </p>
        </div>
      </section>

      <section className="services-cta">
        <div className="cta-container">
          <Link to="/contact" className="btn-primary">
            Get Started
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;